import { useEffect, useRef, useState } from 'react';

import { CopyIcon } from '@braskai/icons';
import { Button, Input, Modal, Typography, useDeviceType } from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Drawer, IconButton, Stack } from '@mui/material';

import { FeaturedIcon } from '@/shared/assets/icons/FeaturedIcon';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { useRecordingShareAutoFetch } from '../model/useEditorShareAutoFetch';
import { useRecordingShareModalStore } from '../model/useRecordingShareModalStore';

type RecordingShareModalInnerProps = {
  copyLink: string;
  isOpen: boolean;
  recordingId: null | string;
  onClose: () => void;
};

const RecordingShareModalInner = ({
  copyLink,
  isOpen,
  recordingId,
  onClose,
}: RecordingShareModalInnerProps) => {
  const { isMobile } = useDeviceType();
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (isOpen && recordingId) {
      MixpanelEvents.recordingShareModalShown({ recording_id: recordingId, recordingId });
    }
  }, [isOpen, recordingId]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const handleCopyLink = async () => {
    if (recordingId) {
      MixpanelEvents.recordingShareCopyLinkClick({ recording_id: recordingId, recordingId });
    }
    try {
      await navigator.clipboard.writeText(copyLink);
      setIsCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const content = (
    <Stack
      sx={{
        backgroundColor: 'common.white',
        borderRadius: 16,
        paddingX: isMobile ? 16 : 24,
        paddingY: 24,
        position: 'relative',
        width: isMobile ? undefined : 480,
      }}
      direction="column"
      gap={isMobile ? 12 : 16}
    >
      <IconButton
        aria-label="Close share modal"
        sx={{ color: 'grey.300', position: 'absolute', right: isMobile ? 16 : 24, top: 24 }}
        onClick={onClose}
      >
        <Close height={24} width={24} />
      </IconButton>

      <Stack>
        <FeaturedIcon height={56} width={56} />
      </Stack>
      <Stack flexDirection="column" gap={4}>
        <Typography color="grey.900" variant="title-4-md">
          Share your video
        </Typography>
        <Typography color="grey.500" variant="body-2">
          Send your video to anyone using the link below
        </Typography>
      </Stack>
      <Stack>
        <Stack direction="row" sx={{ alignItems: 'stretch', marginTop: isMobile ? 8 : 0 }}>
          <Input
            sx={{ borderRadius: '8px 0 0 8px', flex: 1 }}
            value={copyLink}
            readOnly
          />
          <Button
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderLeft: 'none !important',
              borderRadius: '0 8px 8px 0 !important',
            }}
            variant="outlined"
            onClick={handleCopyLink}
          >
            <Stack alignItems="center" direction="row" gap={8}>
              <CopyIcon height={20} width={20} />
              {isCopied ? 'Copied!' : 'Copy'}
            </Stack>
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );

  if (isMobile) {
    return (
      <Drawer
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        anchor="bottom"
        open={isOpen}
        onClose={onClose}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      open={isOpen}
      onClose={onClose}
    >
      {content}
    </Modal>
  );
};

export const RecordingShareModal = () => {
  const isOpen = useRecordingShareModalStore((state) => state.isOpen);
  const shareUrl = useRecordingShareModalStore((state) => state.shareUrl);
  const recordingId = useRecordingShareModalStore((state) => state.recordingId);
  const onClose = useRecordingShareModalStore((state) => state.close);

  useRecordingShareAutoFetch(recordingId, { enabled: isOpen });

  return (
    <RecordingShareModalInner
      copyLink={shareUrl}
      isOpen={isOpen}
      recordingId={recordingId}
      onClose={onClose}
    />
  );
};
